/**
 * 审计日志服务 - 用户操作与报错上报服务器，并兼容本地缓存
 * 优先写入服务器，失败时写入 localStorage 并触发事件供审计日志页展示
 */

const STORAGE_KEY = 'audit_logs';
const RECENT_ACTIONS_KEY = 'audit_recent_actions';
const API_BASE = '/api/audit-logs';
const MAX_LOCAL_LOGS = 2000;
const MAX_RECENT_ACTIONS = 50;

function getAuthHeader() {
  try {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

function getCurrentUser() {
  try {
    const user = JSON.parse(localStorage.getItem('audit_portal_user') || '{}');
    return { id: user.id || 'unknown', username: user.username || user.name || 'unknown' };
  } catch {
    return { id: 'unknown', username: 'unknown' };
  }
}

function getLocalLogs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocalLogs(logs) {
  try {
    const trimmed = logs.length > MAX_LOCAL_LOGS ? logs.slice(0, MAX_LOCAL_LOGS) : logs;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.warn('审计日志写入本地失败', e);
  }
}

function getRecentActions() {
  try {
    const raw = localStorage.getItem(RECENT_ACTIONS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveRecentActions(actions) {
  try {
    const trimmed = actions.length > MAX_RECENT_ACTIONS ? actions.slice(0, MAX_RECENT_ACTIONS) : actions;
    localStorage.setItem(RECENT_ACTIONS_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.warn('最近操作写入本地失败', e);
  }
}

/**
 * 追加一条审计日志（操作或报错）
 * @param {string} operation - 操作类型：login, logout, create, update, delete, view, export, import, approve, reject, config, error
 * @param {string} module - 模块：project-manager, risk-matrix, internal-control, 等
 * @param {object} [details] - 附加信息
 * @param {{ type?: 'operation'|'error', errorMessage?: string, status?: 'success'|'failure' }} [options]
 * @returns {Promise<string|null>} 日志 id 或 null
 */
export async function addLog(operation, module, details = {}, options = {}) {
  const { type = 'operation', errorMessage, status } = options;
  const user = getCurrentUser();
  const payload = {
    operation,
    module: module || 'system',
    type: type === 'error' ? 'error' : 'operation',
    details: {
      ...details,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      url: typeof window !== 'undefined' ? window.location.href : '',
    },
    errorMessage: errorMessage || null,
    status: status === 'failure' ? 'failure' : 'success',
  };

  const logEntry = {
    id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    timestamp: new Date().toISOString(),
    userId: user.id,
    username: user.username,
    operation: payload.operation,
    module: payload.module,
    type: payload.type,
    details: payload.details,
    errorMessage: payload.errorMessage,
    status: payload.status,
    ip: 'client',
  };

  const recentActions = getRecentActions();
  recentActions.unshift(logEntry);
  saveRecentActions(recentActions);

  try {
    const res = await fetch(API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      credentials: 'include',
      body: JSON.stringify({
        operation: payload.operation,
        module: payload.module,
        type: payload.type,
        details: payload.details,
        errorMessage: payload.errorMessage,
        status: payload.status,
      }),
    });
    if (res.ok) {
      const data = await res.json();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('audit-log-added', { detail: { ...logEntry, id: data.id } }));
      }
      return data.id;
    }
  } catch (e) {
    console.warn('审计日志上报服务器失败，写入本地', e);
  }

  const logs = getLocalLogs();
  logs.unshift(logEntry);
  saveLocalLogs(logs);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('audit-log-added', { detail: logEntry }));
  }
  return logEntry.id;
}

/**
 * 查询审计日志（优先从服务器拉取）
 * @param {{ page?: number, limit?: number, userId?: string, module?: string, operation?: string, type?: string, startDate?: string, endDate?: string }}
 */
export async function getLogs(params = {}) {
  try {
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.userId) q.set('userId', params.userId);
    if (params.module) q.set('module', params.module);
    if (params.operation) q.set('operation', params.operation);
    if (params.type) q.set('type', params.type);
    if (params.startDate) q.set('startDate', params.startDate);
    if (params.endDate) q.set('endDate', params.endDate);
    const res = await fetch(`${API_BASE}?${q.toString()}`, {
      headers: getAuthHeader(),
      credentials: 'include',
    });
    if (res.ok) {
      const data = await res.json();
      const serverItems = Array.isArray(data?.items)
        ? data.items
        : (Array.isArray(data?.logs) ? data.logs : []);
      const normalizedItems = serverItems.map((log) => ({
        ...log,
        timestamp: log?.timestamp || log?.createdAt || new Date().toISOString(),
      }));
      return {
        source: 'server',
        items: normalizedItems,
        logs: normalizedItems,
        total: Number.isFinite(data?.total) ? data.total : normalizedItems.length,
        page: Number.isFinite(data?.page) ? data.page : (params.page || 1),
        limit: Number.isFinite(data?.limit) ? data.limit : (params.limit || 100),
      };
    }
  } catch (e) {
    console.warn('从服务器加载审计日志失败，使用本地', e);
  }
  const local = getLocalLogs();
  const { page = 1, limit = 100, userId, module, operation, type, startDate, endDate } = params;
  let items = local;
  if (userId) items = items.filter((l) => l.userId === userId);
  if (module) items = items.filter((l) => l.module === module);
  if (operation) items = items.filter((l) => l.operation === operation);
  if (type) items = items.filter((l) => (l.type || 'operation') === type);
  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    items = items.filter((l) => new Date(l.timestamp) >= start);
  }
  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    items = items.filter((l) => new Date(l.timestamp) <= end);
  }
  const total = items.length;
  const start = (page - 1) * limit;
  return { source: 'local', items: items.slice(start, start + limit), total, page, limit };
}

/**
 * 记录前端报错（供全局 onerror / unhandledrejection 调用）
 */
export function logError(message, source, lineno, colno, error) {
  const errorMessage = [message, error?.stack].filter(Boolean).join('\n');
  addLog('error', 'system', { source, lineno, colno }, { type: 'error', errorMessage, status: 'failure' }).catch(() => {});
}

/**
 * 清空服务器审计日志（仅管理员）；并清空本地缓存
 */
export async function clearLogs() {
  try {
    const res = await fetch(API_BASE, {
      method: 'DELETE',
      headers: getAuthHeader(),
      credentials: 'include',
    });
    if (res.ok) return true;
  } catch (e) {}
  return false;
}

export async function exportIpWhitelistBlocks(params = {}) {
  const q = new URLSearchParams();
  if (params.startDate) q.set('startDate', params.startDate);
  if (params.endDate) q.set('endDate', params.endDate);
  const res = await fetch(`${API_BASE}/ip-whitelist-blocks/export?${q.toString()}`, {
    headers: getAuthHeader(),
    credentials: 'include',
  });
  if (!res.ok) {
    let message = '导出白名单命中日志失败';
    try {
      const json = await res.json();
      if (json?.error) message = json.error;
    } catch (_) {}
    throw new Error(message);
  }
  return await res.blob();
}
